import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useStore';
import { collaborative as collaborativeApi } from '../lib/api';
import { ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

export default function CollaborativePosts() {
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token)!;
  const [activeTab, setActiveTab] = useState<'opportunities' | 'posts'>('opportunities');
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  async function loadData() {
    setLoading(true);
    try {
      if (activeTab === 'opportunities') {
        const data = await collaborativeApi.getOpportunities(token);
        setOpportunities(data);
      } else {
        const data = await collaborativeApi.getPosts(token, { status: 'all' });
        setPosts(data);
      }
    } catch (error) {
      console.error('loadData error:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  async function handleStartPost(articleId: string, articleTitle: string) {
    const title = prompt(`Title for collaborative post about "${articleTitle}"?`, articleTitle);
    if (!title) return;

    try {
      const post = await collaborativeApi.createPost(token, {
        triggerArticleId: articleId,
        title,
      });
      toast.success('Collaborative post created!');
      navigate(`/collaborate/edit/${post.id}`);
    } catch (error) {
      toast.error('Failed to create post');
    }
  }

  return (
    <div className="min-h-full bg-canvas page-transition">
      <div className="pl-4 lg:pl-6 pr-4 pt-8 pb-5">
        <div className="kicker mb-1">Collaborate · beta</div>
        <h1 className="text-3xl">Write together.</h1>
        <p className="mt-2 text-ink-secondary max-w-prose">Posts built with other readers around the articles people are discussing.</p>
      </div>

      {/* Tab rail */}
      <div className="sticky top-0 z-10 bg-canvas">
        <div className="rail">
          <button
            onClick={() => setActiveTab('opportunities')}
            className={`rail-tab ${activeTab === 'opportunities' ? 'is-active' : ''}`}
          >
            Hot articles
          </button>
          <button
            onClick={() => setActiveTab('posts')}
            className={`rail-tab ${activeTab === 'posts' ? 'is-active' : ''}`}
          >
            All posts
          </button>
        </div>
      </div>

      <div>
        {loading ? (
          <div className="pl-4 lg:pl-6 py-10"><span className="kicker">Loading…</span></div>
        ) : activeTab === 'opportunities' ? (
          <div>
            {opportunities.length === 0 ? (
              <div className="pl-4 lg:pl-6 pr-4 py-10 max-w-list">
                <div className="kicker mb-1">Empty</div>
                <p className="text-ink-secondary">No hot articles yet. Articles with 3+ unique commenters show up here.</p>
              </div>
            ) : (
              opportunities.map((opp) => (
                <div key={opp.id} className="pl-4 lg:pl-6 pr-4 py-4 border-b border-edge-subtle hover:bg-surface-sunken">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 max-w-list">
                      <div className="kicker">
                        {opp.feed.title} · {opp.uniqueCommenters} discussing · {opp.commentCount} comments
                        {opp.hasCollaborativePost && <> · <span className="text-accent-600">Post exists</span></>}
                      </div>
                      <h3 className="font-sans text-[18px] font-semibold leading-snug tracking-normal text-ink">{opp.title}</h3>
                      {opp.excerpt && <p className="text-sm text-ink-secondary mt-1 line-clamp-2">{opp.excerpt}</p>}
                    </div>
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <button onClick={() => navigate(`/article/${opp.id}`)} className="btn">
                        <ExternalLink className="w-3.5 h-3.5" /> View
                      </button>
                      {!opp.hasCollaborativePost && (
                        <button onClick={() => handleStartPost(opp.id, opp.title)} className="btn btn-ink">
                          Start post
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div>
            {posts.length === 0 ? (
              <div className="pl-4 lg:pl-6 pr-4 py-10 max-w-list">
                <div className="kicker mb-1">Empty</div>
                <p className="text-ink-secondary">No collaborative posts yet.</p>
              </div>
            ) : (
              posts.map((post) => (
                <div
                  key={post.id}
                  onClick={() => navigate(post.status === 'draft' ? `/collaborate/edit/${post.id}` : `/collaborate/${post.id}`)}
                  className="pl-4 lg:pl-6 pr-4 py-4 border-b border-edge-subtle hover:bg-surface-sunken cursor-pointer"
                >
                  <div className="max-w-list min-w-0">
                    <div className="kicker">
                      <span className={post.status === 'published' ? 'text-pine' : 'text-signal'}>{post.status}</span>
                      {post.isPublic && <> · public</>}
                      {' · '}{post.authors.length} contributors · {post._count.sections} sections
                    </div>
                    <h3 className="font-sans text-[18px] font-semibold leading-snug tracking-normal text-ink">{post.title}</h3>
                    <p className="text-sm text-ink-secondary mt-1 truncate">About: {post.triggerArticle.title}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
